import { useState } from "react";

import { useApiList, useMutation } from "../api/hooks";
import { CrudForm, type FieldSpec } from "./CrudPage";
import { Badge, Bool, Money, useConfirm, useToast } from "./ui";

export interface ManagedOption {
  id: string;
  option_group_id: string;
  name: string;
  description: string | null;
  price: string;
  is_active: boolean;
}
export interface ManagedOptionGroup {
  id: string;
  name: string;
  multiple: boolean;
  required: boolean;
  is_active: boolean;
  options: ManagedOption[];
}

const GROUP_FIELDS: FieldSpec[] = [
  { name: "name", label: "Name (Portion, Extras…)", required: true },
  { name: "multiple", label: "Customers can pick several", type: "switch", defaultValue: false },
  { name: "required", label: "A choice is required", type: "switch", defaultValue: false },
  { name: "is_active", label: "Active", type: "switch", editOnly: true },
];
const OPTION_FIELDS: FieldSpec[] = [
  { name: "name", label: "Name (Large, Extra cheese…)", required: true },
  { name: "price", label: "Extra price", type: "number", decimal: true },
  { name: "description", label: "Description" },
  { name: "image_url", label: "Image", type: "image" },
  { name: "is_active", label: "Active", type: "switch", editOnly: true },
];

/** Option groups and options of one vendor (`/vendor/option-groups`, `/vendor/options`). */
export function OptionGroups({ vendorId }: { vendorId: string }) {
  const list = useApiList<ManagedOptionGroup>("/vendor/option-groups", { vendor_id: vendorId });
  const { run, busy } = useMutation();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [groupForm, setGroupForm] = useState<ManagedOptionGroup | "new" | null>(null);
  const [optionForm, setOptionForm] = useState<{ group: ManagedOptionGroup; option: ManagedOption | null } | null>(null);
  const q = { vendor_id: vendorId };

  async function act(path: string, method: "POST" | "PATCH" | "DELETE", body?: unknown, done = "Saved") {
    const r = await run(path, { method, body, query: q });
    if (r.ok) {
      toast(done);
      list.reload();
    } else toast(r.error, "err");
    return r.ok;
  }

  return (
    <>
      <div className="page-head">
        <h2>Option groups</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setGroupForm("new")}>
          + Group
        </button>
      </div>
      {list.error && <p className="error">{list.error}</p>}
      {!list.loading && list.data.length === 0 && <p className="muted">No option groups yet.</p>}
      {list.data.map((g) => (
        <div key={g.id} className="option-group">
          <div className="option-group-head">
            <strong>{g.name}</strong>
            <Badge tone="info">{g.multiple ? "pick several" : "pick one"}</Badge>
            <Badge tone={g.required ? "warn" : "neutral"}>{g.required ? "required" : "optional"}</Badge>
            {!g.is_active && <Badge tone="bad">hidden</Badge>}
            <span className="row-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOptionForm({ group: g, option: null })}>
                + Option
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setGroupForm(g)}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm danger"
                onClick={async () => {
                  if (await confirm(`Remove "${g.name}" and its ${g.options.length} option(s)?`)) await act(`/vendor/option-groups/${g.id}`, "DELETE", undefined, "Group removed");
                }}
              >
                Delete
              </button>
            </span>
          </div>
          <table className="table table-plain">
            <tbody>
              {g.options.map((o) => (
                <tr key={o.id}>
                  <td>
                    {o.name}
                    {o.description && <span className="muted"> · {o.description}</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Money value={o.price} />
                  </td>
                  <td>
                    <Bool value={o.is_active} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <span className="row-actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOptionForm({ group: g, option: o })}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm danger" onClick={() => act(`/vendor/options/${o.id}`, "DELETE", undefined, "Option removed")}>
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {groupForm !== null && (
        <CrudForm
          title={groupForm === "new" ? "New option group" : `Edit ${groupForm.name}`}
          fields={GROUP_FIELDS}
          initial={groupForm === "new" ? {} : (groupForm as unknown as Record<string, unknown>)}
          busy={busy}
          onClose={() => setGroupForm(null)}
          onSubmit={async (values) => {
            const ok = groupForm === "new" ? await act("/vendor/option-groups", "POST", values) : await act(`/vendor/option-groups/${groupForm.id}`, "PATCH", values);
            if (ok) setGroupForm(null);
          }}
        />
      )}
      {optionForm && (
        <CrudForm
          title={optionForm.option ? `Edit ${optionForm.option.name}` : `New option in ${optionForm.group.name}`}
          fields={OPTION_FIELDS}
          initial={optionForm.option ? (optionForm.option as unknown as Record<string, unknown>) : {}}
          busy={busy}
          onClose={() => setOptionForm(null)}
          onSubmit={async (values) => {
            const ok = optionForm.option
              ? await act(`/vendor/options/${optionForm.option.id}`, "PATCH", values)
              : await act("/vendor/options", "POST", { ...values, option_group_id: optionForm.group.id });
            if (ok) setOptionForm(null);
          }}
        />
      )}
      {dialog}
    </>
  );
}
